import {Root, Item} from '@radix-ui/react-radio-group'
import React from 'react'
import styled from 'styled-components'

import {Text} from '@/components/text'
import {tokens, colors, radii, focusRingSizes, zIndex} from '@/theme'
import mq from '@/utils/media-query'

interface PlanSelectionOverlayProps {
    content: {
        title: string
        options: {
            value: string
            label: string
            description: string
        }[]
    }
    selectedValue: string
    onChange?: (value: any) => void
}

export const PlanSelectionOverlay = ({
    content,
    selectedValue,
    onChange,
}: PlanSelectionOverlayProps) => (
    <Wrapper defaultValue={selectedValue} onValueChange={(value) => onChange && onChange(value)}>
        <Text $variant="label-base">{content.title}</Text>
        {content.options.map(({value, label, description}, index) => (
            <Option value={value} key={`${value}${index}`} $checked={value === selectedValue}>
                <PlanContent>
                    <Indicator />
                    <PlanTitle $variant="paragraph-base" $color="muted">
                        {label}
                    </PlanTitle>
                    <PlanDescription $variant="paragraph-base" $color="muted">
                        {description}
                    </PlanDescription>
                </PlanContent>
            </Option>
        ))}
    </Wrapper>
)

interface PlanSelectionOptionProps {
    $checked: boolean
}

const Wrapper = styled(Root)`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacing16};
    padding: ${tokens.spacing20};
    height: 100svh;
    position: fixed;
    top: 145px;
    width: 100%;
    background: ${({theme}) => theme.colors.background.default};
    z-index: ${zIndex.tooltip};

    ${mq.min('sm')} {
        width: calc(100% - 48px);
    }
`

const PlanContent = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    column-gap: ${tokens.spacing8};
    row-gap: 0;
    align-items: center;
`

const PlanTitle = styled(Text)`
    font-weight: ${tokens.fontWeightBold};
    font-size: 18px;
    grid-row: 1;
    justify-self: start;
`

const PlanDescription = styled(Text)`
    grid-column: 2;
    grid-row: 2;
`

const Indicator = styled.span`
    width: ${tokens.spacing18};
    height: ${tokens.spacing18};
    border: ${tokens.spacing2} solid ${colors.border.dark};
    background-color: ${colors.background.default};
    border-radius: ${radii.circle};
    transition: border-color ${tokens.fastInOut};
    grid-row: 1;
`

const Option = styled(Item)<PlanSelectionOptionProps>`
    display: flex;
    gap: ${tokens.spacing12};
    border: ${({$checked}) =>
        $checked ? `1px solid ${colors.border.secondary}` : `1px solid ${colors.border.dark}`};
    background-color: ${({$checked}) =>
        $checked ? colors.background.muted : colors.background.default};
    padding: ${tokens.spacing12};
    border-radius: ${radii.arc20};
    cursor: pointer;

    &[data-state='checked'] {
        ${Indicator} {
            border-width: 5px;
            border-color: ${colors.border.secondary};
        }
    }

    &:focus {
        outline: transparent solid 2px;
        box-shadow: 0 0 0 ${focusRingSizes['thin']} ${colors.focusRing};
    }
`
